import React, { useState, useEffect } from 'react';
import { Building2, Search, GraduationCap, ExternalLink, MapPin, Award, BookOpen, Sparkles, ArrowLeft, ChevronDown, ChevronUp, FileText, List, Circle, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  extractDegreePrefix,
  calculateScore,
  cleanQueryForKeywords,
  extractWordsWithMetadata,
  SEARCH_CONFIG,
  getPrefixVariant
} from '../utils/rncpSearchUtils';

// Styles pour l'animation
const styles = `
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .blur-15 {
    filter: blur(1.5px);
  }
`;

// Injecter les styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

// Composant skeleton pour les cartes de formation
const FormationSkeletonCard = ({ index, isBlurred }) => {
  return (
    <div
      className={`border border-gray-200 rounded-lg p-5 transition-all duration-500 ${
        isBlurred ? 'blur-sm' : 'blur-none'
      }`}
      style={{
        animation: `slideDown 0.5s ease-out ${index * 0.05}s both`
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <Circle className="w-6 h-6 text-gray-300 animate-pulse" />
        <div className="flex-1">
          <div className="h-6 bg-gray-200 rounded w-3/4 mb-3 animate-pulse"></div>
          <div className="flex flex-wrap gap-2 mb-3">
            <div className="h-6 bg-gray-100 rounded-full w-24 animate-pulse"></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="h-4 bg-gray-100 rounded w-full animate-pulse"></div>
            <div className="h-4 bg-gray-100 rounded w-full animate-pulse"></div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="h-10 bg-gray-200 rounded w-24 animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

// ⚠️ FONCTIONS DE RECHERCHE MAINTENANT DANS utils/rncpSearchUtils.js
// Les fonctions extractDegreePrefix et calculateScore sont importées depuis le fichier utilitaire

// Fonction de scoring côté client (alias vers la fonction importée)
const calculateClientScore = (query, title, rncpCode = '', isActive = true) => {
  return calculateScore(query, title, rncpCode, isActive);
};

// Ancien code supprimé - utilise maintenant les fonctions de utils/rncpSearchUtils.js
const oldCalculateClientScore = (query, title, rncpCode = '', isActive = true) => {
  const normalizeStr = (str) => {
    if (!str) return '';
    return str.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, ' ')
      // Normaliser TP et Titre professionnel pour la comparaison
      .replace(/\btitre professionnel\b/gi, 'tp')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const extractWords = (str) => {
    const stopWords = ['le', 'la', 'les', 'un', 'une', 'des', 'de', 'du', 'et', 'ou', 'en', 'a', 'au', 'aux', 'pour', 'par', 'dans', 'sur', 'avec'];
    return normalizeStr(str).split(' ').filter(w => w.length > 2 && !stopWords.includes(w));
  };

  // Extraire le préfixe de diplôme
  const { prefix, coreQuery } = extractDegreePrefix(query);
  const searchQuery = prefix ? coreQuery : query;

  const normalizedQuery = normalizeStr(searchQuery);
  const normalizedTitle = normalizeStr(title);
  const queryWords = extractWords(searchQuery);
  const titleWords = extractWords(title);

  let score = 0;

  // 1. Correspondance exacte
  if (normalizedQuery === normalizedTitle) return 100;

  // 2. Correspondance code RNCP
  if (rncpCode && normalizedQuery.replace(/\s+/g, '') === rncpCode.toLowerCase()) {
    return 95;
  }

  // 3. Titre commence par la requête
  if (normalizedTitle.startsWith(normalizedQuery)) {
    score = 80;
  }
  // 4. Titre contient la requête complète
  else if (normalizedTitle.includes(normalizedQuery)) {
    score = 60;
  }

  // 5. Analyse avec pondération
  const genericWords = ['bac', 'pro', 'master', 'bts', 'cap', 'diplome', 'titre', 'professionnel', 'conduite', 'production', 'productions', 'gestion', 'technique', 'techniques', 'responsable', 'charge', 'chef', 'agent', 'arts', 'metiers', 'metier', 'service', 'services', 'certificat', 'specialisation', 'manager', 'unite', 'unites'];
  const commonWords = [];
  const missingWords = [];

  queryWords.forEach(qw => {
    const weight = genericWords.includes(qw) ? 0.3 : 1.0;
    const found = titleWords.some(tw => tw.includes(qw) || qw.includes(tw));

    if (found) {
      commonWords.push({ word: qw, weight });
    } else {
      missingWords.push({ word: qw, weight });
    }
  });

  const totalWeight = queryWords.reduce((sum, w) =>
    sum + (genericWords.includes(w) ? 0.3 : 1.0), 0
  );
  const matchedWeight = commonWords.reduce((sum, item) => sum + item.weight, 0);
  const missingWeight = missingWords.reduce((sum, item) => sum + item.weight, 0);
  const ratio = totalWeight > 0 ? matchedWeight / totalWeight : 0;

  // Règle stricte : < 50% de correspondance
  if (ratio < 0.5) {
    score = Math.max(score, ratio * 30);
    if (missingWeight > 0.5) {
      score *= 0.5;
    }
    return Math.min(100, Math.max(0, score));
  }

  // Score basé sur le ratio
  const wordScore = ratio * 60;
  score = Math.max(score, wordScore);

  // Bonus si tous les mots trouvés
  if (commonWords.length === queryWords.length) {
    score += 20;
  }

  // Bonus ordre des mots
  let orderScore = 0;
  let lastIndex = -1;
  queryWords.forEach(qw => {
    const idx = titleWords.findIndex((tw, i) =>
      i > lastIndex && (tw.includes(qw) || qw.includes(tw))
    );
    if (idx > lastIndex) {
      orderScore += 5;
      lastIndex = idx;
    }
  });
  score += orderScore;

  // NOUVELLE PÉNALITÉ : Mots supplémentaires dans le titre
  // Si le titre contient beaucoup de mots qui ne sont PAS dans la requête, c'est suspect
  const extraWords = titleWords.filter(tw =>
    !queryWords.some(qw => tw.includes(qw) || qw.includes(tw))
  ).length;

  const extraWordsRatio = titleWords.length > 0 ? extraWords / titleWords.length : 0;

  // Si plus de 50% des mots du titre sont des "extras", pénaliser fortement
  if (extraWordsRatio > 0.5) {
    score *= (1 - extraWordsRatio * 0.5); // Réduction jusqu'à -50%
  }

  // Pénalité titres longs
  const lengthRatio = normalizedQuery.length / normalizedTitle.length;
  if (lengthRatio < 0.3) {
    score *= 0.9;
  }

  // Pénalité si nombre de mots très différent
  const wordCountRatio = Math.min(queryWords.length, titleWords.length) /
                         Math.max(queryWords.length, titleWords.length);
  if (wordCountRatio < 0.5) {
    score *= 0.85; // -15%
  }

  // Bonus acronymes
  const queryAcronyms = searchQuery.match(/\b[A-Z]{2,}\b/g) || [];
  const titleAcronyms = title.match(/\b[A-Z]{2,}\b/g) || [];
  const matchingAcronyms = queryAcronyms.filter(qa =>
    titleAcronyms.some(ta => ta === qa)
  );
  if (matchingAcronyms.length > 0) {
    score += matchingAcronyms.length * 5;
  }

  // ✅ BONUS/PÉNALITÉ PRÉFIXE : Valoriser le BON préfixe, pénaliser le MAUVAIS
  if (prefix) {
    const normalizedPrefix = normalizeStr(prefix);
    const normalizedTitleFull = normalizeStr(title);

    let titlePrefix = null;

    // 1️⃣ D'abord : Chercher dans la liste de préfixes connus (début/fin avec tiret, etc.)
    const allPrefixes = [
      'certificat de specialisation', 'diplome d etat', 'diplome d\'etat', 'titre professionnel',
      'bac pro', 'bts', 'cap', 'bac', 'master', 'licence',
      'tp', 'dut', 'but', 'bep', 'mc', 'doctorat', 'deust', 'de', 'cs', 'bp', 'btm'
    ];

    for (const p of allPrefixes) {
      const patterns = [
        new RegExp(`-\\s*${p}\\s*$`, 'i'),       // - BTS à la fin
        new RegExp(`^${p}\\s*-`, 'i'),            // BTS - au début
        new RegExp(`^${p}\\b`, 'i')               // BTS au début
      ];

      if (patterns.some(pattern => pattern.test(normalizedTitleFull))) {
        titlePrefix = p;
        break;
      }
    }

    // 2️⃣ Ensuite : Chercher TOUT ce qui est entre parenthèses (prioritaire)
    const parenthesesMatch = title.match(/\(([^)]+)\)/);
    if (parenthesesMatch) {
      // Préfixe trouvé entre parenthèses → remplace le précédent
      titlePrefix = normalizeStr(parenthesesMatch[1]);
    }

    if (titlePrefix) {
      // Si un préfixe est détecté dans le titre
      if (titlePrefix === normalizedPrefix || titlePrefix.includes(normalizedPrefix) || normalizedPrefix.includes(titlePrefix)) {
        // ✅ BON préfixe (match exact ou contient)
        score += 30; // +30 points
      } else {
        // ❌ MAUVAIS préfixe (ex: cherche BTS, trouve MASTER ou MC NIVEAU IV)
        score -= 30; // -30 points de pénalité
        console.log(`  ❌ PÉNALITÉ PRÉFIXE -30: cherche "${prefix}", trouve "${titlePrefix}"`);
      }
    } else {
      // Pas de préfixe détecté dans le titre, pas de bonus/pénalité
    }
  }

  // ✅ BONUS/PÉNALITÉ OPTION (A, B, C, etc.)
  // Détecter "option A", "option B", etc. dans la requête ET le titre
  const optionRegex = /option\s+([A-Z])\b/i;
  const queryOptionMatch = query.match(optionRegex);
  const titleOptionMatch = title.match(optionRegex);

  if (queryOptionMatch && titleOptionMatch) {
    const queryOption = queryOptionMatch[1].toUpperCase();
    const titleOption = titleOptionMatch[1].toUpperCase();

    if (queryOption === titleOption) {
      // ✅ Même option
      score += 15;
      console.log(`  ✅ BONUS OPTION +15: même option "${queryOption}"`);
    } else {
      // ❌ Option différente
      score -= 25;
      console.log(`  ❌ PÉNALITÉ OPTION -25: cherche option "${queryOption}", trouve option "${titleOption}"`);
    }
  } else if (queryOptionMatch && !titleOptionMatch) {
    // Cherche une option spécifique mais le titre n'en a pas → pénalité légère
    score -= 10;
    console.log(`  ⚠️  PÉNALITÉ OPTION -10: cherche option "${queryOptionMatch[1]}", titre sans option`);
  }

  // Limiter d'abord le score à 100
  score = Math.min(100, score);

  // PUIS appliquer la pénalité pour les certifications inactives
  if (!isActive) {
    score -= 20;
  }

  return Math.max(0, score);
};

// Composant pour l'icône de statut d'enrichissement
const EnrichmentStatusIcon = ({ status, onClick }) => {
  const configs = {
    pending: {
      icon: Circle,
      className: "w-6 h-6 text-gray-400",
      title: "En attente"
    },
    loading: {
      icon: Loader,
      className: "w-6 h-6 text-blue-500 animate-spin",
      title: "Recherche en cours..."
    },
    success: {
      icon: CheckCircle,
      className: "w-6 h-6 text-green-500",
      title: "RNCP trouvé"
    },
    not_found: {
      icon: AlertCircle,
      className: "w-6 h-6 text-orange-500 cursor-pointer hover:text-orange-600",
      title: "RNCP non trouvé - Cliquer pour rechercher manuellement"
    }
  };

  const config = configs[status] || configs.pending;
  const Icon = config.icon;

  return (
    <div
      onClick={status === 'not_found' ? onClick : undefined}
      title={config.title}
      className={status === 'not_found' ? 'cursor-pointer' : ''}
    >
      <Icon className={config.className} />
    </div>
  );
};

// Composant de barre de progression intelligente avec pourcentage orange
const SmartProgressBar = ({ isActive, stage, enrichmentProgress, formations, onProgressChange }) => {
  const [progress, setProgress] = useState(0);

  // Notifier le parent du changement de progression
  useEffect(() => {
    if (onProgressChange) {
      onProgressChange(progress);
    }
  }, [progress, onProgressChange]);

  // Progression simulée avec vitesses variables
  useEffect(() => {
    if (!isActive) {
      // Compléter à 100% puis reset
      if (progress > 0 && progress < 100) {
        setProgress(100);
        const timeout = setTimeout(() => setProgress(0), 500);
        return () => clearTimeout(timeout);
      }
      return;
    }

    // Si on a un enrichissement en cours, utiliser la progression réelle
    if (enrichmentProgress && enrichmentProgress.total > 0) {
      const realProgress = (enrichmentProgress.current / enrichmentProgress.total) * 100;
      setProgress(realProgress);
      return;
    }

    // Sinon, simuler la progression (scraping initial)
    // 0-30% : Lent (0.3% toutes les 200ms)
    // 30-50% : Moyen (0.5% toutes les 200ms)
    // 50-70% : Plus lent (0.3% toutes les 300ms)
    // 70-90% : Très lent (0.2% toutes les 400ms)
    // 90-95% : Attente serveur
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev < 30) {
          return prev + 0.3; // Lent jusqu'à 30%
        } else if (prev < 50) {
          return prev + 0.5; // Accélère un peu jusqu'à 50%
        } else if (prev < 70) {
          return prev + 0.3; // Ralentit jusqu'à 70%
        } else if (prev < 90) {
          return prev + 0.2; // Très lent jusqu'à 90%
        } else if (prev < 95) {
          return prev + 0.1; // Crawl jusqu'à 95%
        } else {
          return 95; // Bloqué à 95% en attendant la réponse
        }
      });
    }, 200);

    return () => clearInterval(interval);
  }, [isActive, enrichmentProgress, progress]);

  // Calculer le pourcentage orange (formations not_found)
  const notFoundCount = formations?.filter(f => f.enrichmentStatus === 'not_found').length || 0;
  const successCount = formations?.filter(f => f.enrichmentStatus === 'success').length || 0;
  const total = formations?.length || 1;

  const notFoundPercent = (notFoundCount / total) * 100;
  const successPercent = (successCount / total) * 100;

  if (!isActive && progress === 0) return null;

  const stageText = stage === 'search'
    ? 'Recherche de l\'organisme sur Intercarif Oref...'
    : stage === 'formations'
    ? 'Extraction des formations en cours...'
    : enrichmentProgress
    ? `Enrichissement RNCP: ${enrichmentProgress.current}/${enrichmentProgress.total}`
    : 'Enrichissement des formations avec les codes RNCP...';

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{stageText}</span>
        <span className="text-sm font-semibold text-primary-600">{Math.round(progress)}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden relative">
        {enrichmentProgress && enrichmentProgress.total > 0 ? (
          // Mode enrichissement : afficher vert + orange
          <>
            <div
              className="h-full bg-green-500 absolute left-0 transition-all duration-300 ease-out"
              style={{ width: `${successPercent}%` }}
            />
            <div
              className="h-full bg-orange-500 absolute transition-all duration-300 ease-out"
              style={{ left: `${successPercent}%`, width: `${notFoundPercent}%` }}
            />
          </>
        ) : (
          // Mode scraping : afficher barre bleue animée
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        )}
      </div>
    </div>
  );
};

// Composant pour afficher les blocs de compétences
const CompetenceBlocksView = ({ formation, onClose }) => {
  const [expanded, setExpanded] = useState({});

  const toggleBlock = (blockIndex) => {
    setExpanded(prev => ({
      ...prev,
      [blockIndex]: !prev[blockIndex]
    }));
  };

  if (!formation.rncpData || !formation.rncpData.blocs_competences) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Blocs de compétences</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>
          <p className="text-gray-600">Aucun bloc de compétences disponible pour cette formation.</p>
        </div>
      </div>
    );
  }

  const blocks = formation.rncpData.blocs_competences;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{formation.title}</h3>
              <p className="text-sm text-gray-600 mt-1">
                Code RNCP: {formation.rncpCode || 'Non trouvé'} | {blocks.length} bloc(s) de compétences
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {blocks.map((block, index) => (
            <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => toggleBlock(index)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary-600" />
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">{block.libelle || `Bloc ${index + 1}`}</p>
                    {block.code && (
                      <p className="text-xs text-gray-500">Code: {block.code}</p>
                    )}
                  </div>
                </div>
                {expanded[index] ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {expanded[index] && (
                <div className="p-4 bg-white border-t border-gray-200">
                  {block.liste_competences ? (
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700">
                        {block.liste_competences}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">Aucune compétence détaillée disponible</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Composant pour la vue récapitulative complète
const SummaryListView = ({ formations, organism, onClose }) => {
  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
      <div className="max-w-7xl mx-auto p-6">
        <div className="sticky top-0 bg-white border-b border-gray-200 pb-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                <List className="w-7 h-7 text-primary-600" />
                Liste Complète - {organism.name}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {formations.length} formation(s) avec détails RNCP
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
          </div>
        </div>

        <div className="space-y-8">
          {formations.map((formation, index) => {
            const isNonFoundOrNonRef = !formation.rncpCode || formation.isNonReference;
            const borderColor = isNonFoundOrNonRef ? 'border-orange-300' : 'border-gray-300';
            const bgColor = isNonFoundOrNonRef ? 'bg-orange-50' : 'bg-white';

            return (
            <div key={index} className={`border ${borderColor} rounded-lg p-6 ${bgColor} shadow-sm`}>
              {/* En-tête de la formation */}
              <div className="mb-4">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">
                    {index + 1}. {formation.title}
                  </h3>
                  {formation.url && (
                    <a
                      href={formation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 whitespace-nowrap"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 text-sm">
                  {formation.rncpCode && (
                    <>
                      <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full font-medium">
                        RNCP: {formation.rncpCode}
                      </span>
                      {formation.isNonReference && (
                        <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full font-medium">
                          (non référencé)
                        </span>
                      )}
                      {formation.relevanceScore !== undefined && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                          Score: {Math.round(formation.relevanceScore)}%
                        </span>
                      )}
                    </>
                  )}
                  {!formation.rncpCode && (
                    <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                      RNCP non trouvé
                    </span>
                  )}
                  {formation.code && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                      Code: {formation.code}
                    </span>
                  )}
                  {formation.level && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full">
                      {formation.level}
                    </span>
                  )}
                </div>
              </div>

              {/* Blocs de compétences */}
              {formation.rncpData && formation.rncpData.blocs_competences && formation.rncpData.blocs_competences.length > 0 ? (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary-600" />
                    Blocs de compétences ({formation.rncpData.blocs_competences.length})
                  </h4>
                  {formation.rncpData.blocs_competences.map((block, blockIndex) => (
                    <div key={blockIndex} className="bg-gray-50 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-2">
                        {block.libelle || `Bloc ${blockIndex + 1}`}
                      </h5>
                      {block.code && (
                        <p className="text-xs text-gray-500 mb-2">Code: {block.code}</p>
                      )}
                      {block.liste_competences && (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap">
                          {block.liste_competences}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic">Pas de blocs de compétences disponibles</p>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default function IntercariforefTest() {
  const [siret, setSiret] = useState('');
  const [loadingStage, setLoadingStage] = useState(null);
  const [organism, setOrganism] = useState(null);
  const [formations, setFormations] = useState([]);
  const [error, setError] = useState(null);
  const [selectedFormation, setSelectedFormation] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [enrichmentProgress, setEnrichmentProgress] = useState({ current: 0, total: 0 });
  const [selectedForManualSearch, setSelectedForManualSearch] = useState(null);
  const [skeletonCount, setSkeletonCount] = useState(0);
  const [showSkeletons, setShowSkeletons] = useState(false);

  // Fonction pour formater le SIRET
  const formatSiret = (value) => {
    const numbers = value.replace(/\s/g, '');
    if (numbers.length <= 14) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{5})/, '$1 $2 $3 $4').trim();
    }
    return numbers.substring(0, 14);
  };

  // Fonction pour enrichir une formation avec les données RNCP (avec délai de 3s + scoring)
  const enrichFormationWithRncp = async (formation, index) => {
    try {
      // Délai de 3 secondes entre chaque requête pour éviter le rate limiting
      if (index > 0) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      const apiSearchTerm = formation.title;
      console.log(`\n🔍 Recherche RNCP pour: "${apiSearchTerm}"`);

      // ✅ NOUVEAU : Extraire le préfixe de diplôme DÈS le début
      const { prefix, coreQuery } = extractDegreePrefix(apiSearchTerm);

      if (prefix) {
        console.log(`  📌 Préfixe détecté: "${prefix}"`);
        console.log(`  📌 Requête core: "${coreQuery}"`);
      }

      const baseUrl = `${import.meta.env.VITE_API_URL}/kit-apprentissage/search/intitule`;
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      // RECHERCHE 1 : Titre original complet
      console.log(`  🔍 [TYPE 1] Recherche avec titre complet: "${apiSearchTerm}"`);
      const searchPromises = [
        fetch(`${baseUrl}?q=${encodeURIComponent(apiSearchTerm)}&limit=100`, { headers })
          .then(r => r.json())
          .then(data => ({ ...data, searchType: 1 })) // ✅ Label TYPE 1
          .catch(() => ({ success: false, data: [], searchType: 1 }))
      ];

      // RECHERCHE 2 : Sans le préfixe complet (ex: "Titre professionnel" → juste "conseiller de vente")
      if (prefix && coreQuery && coreQuery !== apiSearchTerm) {
        console.log(`  🔍 [TYPE 2] Recherche sans préfixe "${prefix}": "${coreQuery}"`);
        searchPromises.push(
          fetch(`${baseUrl}?q=${encodeURIComponent(coreQuery)}&limit=100`, { headers })
            .then(r => r.json())
            .then(data => ({ ...data, searchType: 2 })) // ✅ Label TYPE 2
            .catch(() => ({ success: false, data: [], searchType: 2 }))
        );
      }

      // RECHERCHE 3 : Mots-clés spécifiques (jusqu'à 3 mots)
      // ✅ Utilise les constantes partagées depuis rncpSearchUtils
      const { stopWords, genericWords } = SEARCH_CONFIG;

      // ✅ Nettoyer : tirets → espaces, ponctuation → supprimée
      const queryForKeywords = (prefix ? coreQuery : apiSearchTerm)
        .replace(/-/g, ' ')                    // tirets → espaces
        .replace(/[,;:.!?()]/g, ' ')           // virgules, points, etc. → espaces
        .replace(/\s+/g, ' ')                  // espaces multiples → 1 espace
        .trim();

      const words = queryForKeywords.split(' ')
        .filter(w => w.length > 3 && !stopWords.includes(w.toLowerCase()))
        .map((w, index) => ({
          word: w,
          isGeneric: genericWords.includes(w.toLowerCase()),
          length: w.length,
          originalIndex: index // ✅ Conserver l'ordre original
        }));

      // ✅ Filtrer les mots spécifiques SANS trier (garder l'ordre original)
      const specificWords = words.filter(w => !w.isGeneric);

      // ❌ TYPE 3 : 3 mots spécifiques (DÉSACTIVÉ - peu efficace selon tests utilisateur)
      // Résultats tests : 0 formations trouvées
      /*
      if (specificWords.length >= 3) {
        const keyWords = specificWords.slice(0, 3).map(w => w.word).join(' ');
        if (keyWords && keyWords !== apiSearchTerm && keyWords !== coreQuery) {
          console.log(`  🔍 [TYPE 3] Recherche mots-clés spécifiques (3 mots): "${keyWords}"`);
          searchPromises.push(
            fetch(`${baseUrl}?q=${encodeURIComponent(keyWords)}&limit=100`, { headers })
              .then(r => r.json())
              .then(data => ({ ...data, searchType: 3 }))
              .catch(() => ({ success: false, data: [], searchType: 3 }))
          );
        }
      }
      */

      // ✅ TYPE 5 : Seulement les 2 premiers mots (souvent plus efficace avec l'API)
      if (specificWords.length >= 2) {
        const keyWords2 = specificWords.slice(0, 2).map(w => w.word).join(' ');
        if (keyWords2 && keyWords2 !== apiSearchTerm && keyWords2 !== coreQuery) {
          console.log(`  🔍 [TYPE 5] Recherche mots-clés spécifiques (2 mots): "${keyWords2}"`);
          searchPromises.push(
            fetch(`${baseUrl}?q=${encodeURIComponent(keyWords2)}&limit=100`, { headers })
              .then(r => r.json())
              .then(data => ({ ...data, searchType: 5 }))
              .catch(() => ({ success: false, data: [], searchType: 5 }))
          );
        }
      }

      // ✅ TYPE 6 : Recherche avec préfixe + 1 mot spécifique (pour mots simples)
      // Ex: "BTS - cybersécurité" → recherche "BTS cybersécurité"
      if (prefix && specificWords.length >= 1) {
        const firstWord = specificWords[0].word;
        const queryWithPrefix = `${prefix} ${firstWord}`;
        console.log(`  🔍 [TYPE 6] Recherche préfixe + 1er mot: "${queryWithPrefix}"`);
        searchPromises.push(
          fetch(`${baseUrl}?q=${encodeURIComponent(queryWithPrefix)}&limit=100`, { headers })
            .then(r => r.json())
            .then(data => ({ ...data, searchType: 6 }))
            .catch(() => ({ success: false, data: [], searchType: 6 }))
        );
      }

      // ✅ TYPE 7 : Recherche avec variante de préfixe (ex: "Titre professionnel" → aussi chercher avec "TP")
      if (prefix) {
        const prefixVariant = getPrefixVariant(prefix);
        if (prefixVariant && prefixVariant !== prefix.toUpperCase()) {
          const queryWithVariant = `${prefixVariant} ${coreQuery}`;
          console.log(`  🔍 [TYPE 7] Recherche avec variante de préfixe: "${prefix}" → "${prefixVariant}" : "${queryWithVariant}"`);
          searchPromises.push(
            fetch(`${baseUrl}?q=${encodeURIComponent(queryWithVariant)}&limit=100`, { headers })
              .then(r => r.json())
              .then(data => ({ ...data, searchType: 7 }))
              .catch(() => ({ success: false, data: [], searchType: 7 }))
          );
        }
      }

      // ❌ TYPE 4 : Sans première lettre (DÉSACTIVÉ - peu efficace selon tests utilisateur)
      // Résultats tests : 3 formations trouvées (+2 uniques)
      // Performance insuffisante par rapport aux autres types
      /*
      if (coreQuery) {
        const coreWords = coreQuery.split(' ').filter(w => w.length > 0);
        if (coreWords.length > 0 && coreWords[0].length > 1) {
          const firstWordWithoutFirstLetter = coreWords[0].substring(1);
          const queryWithoutFirstLetter = [firstWordWithoutFirstLetter, ...coreWords.slice(1)].join(' ');

          if (queryWithoutFirstLetter && queryWithoutFirstLetter.length > 3) {
            console.log(`  🔍 [TYPE 4] Recherche sans 1ère lettre: "${queryWithoutFirstLetter}"`);
            searchPromises.push(
              fetch(`${baseUrl}?q=${encodeURIComponent(queryWithoutFirstLetter)}&limit=100`, { headers })
                .then(r => r.json())
                .then(data => ({ ...data, searchType: 4 }))
                .catch(() => ({ success: false, data: [], searchType: 4 }))
            );
          }
        }
      }
      */

      // Exécuter les recherches en parallèle
      const responses = await Promise.all(searchPromises);

      // Combiner et dédupliquer les résultats par code RNCP (en gardant le type de recherche)
      let allResults = [];
      const seenRncps = new Map(); // Map pour stocker RNCP → searchType

      responses.forEach(response => {
        if (response.success && response.data) {
          const certs = Array.isArray(response.data) ? response.data : [response.data];
          certs.forEach(cert => {
            const rncpCode = cert.rncp || cert.code_rncp;
            if (rncpCode && !seenRncps.has(rncpCode)) {
              seenRncps.set(rncpCode, response.searchType); // ✅ Stocker le type
              allResults.push({
                ...cert,
                searchType: response.searchType // ✅ Ajouter le type au résultat
              });
            }
          });
        }
      });

      console.log(`  📊 Total résultats combinés: ${allResults.length}`);

      // Créer un objet data compatible avec le reste du code
      const data = {
        success: allResults.length > 0,
        data: allResults
      };

      if (data.success && data.data && data.data.length > 0) {
        // Appliquer le scoring sur tous les résultats
        const scoredResults = data.data.map(cert => {
          const title = cert.intitule_diplome || cert.intitule_rncp || cert.intitule || 'Sans titre';
          const rncpCode = cert.rncp || cert.code_rncp || '';

          // Détecter si le RNCP est inactif
          const isInactive = cert.etat_fiche && (
            cert.etat_fiche.toLowerCase().includes('inactif') ||
            cert.etat_fiche.toLowerCase().includes('inactive') ||
            cert.etat_fiche === 'Inactive'
          );

          // Calculer le score avec pénalité pour les inactifs
          const score = calculateClientScore(formation.title, title, rncpCode, !isInactive);

          return {
            ...cert,
            relevanceScore: score,
            isActive: !isInactive,
            searchType: cert.searchType // ✅ Conserver le type de recherche
          };
        })
        // Filtrer par score minimum de 35
        .filter(cert => cert.relevanceScore >= 35)
        // Trier par score décroissant puis par longueur
        .sort((a, b) => {
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          const titleA = a.intitule_diplome || a.intitule_rncp || a.intitule || '';
          const titleB = b.intitule_diplome || b.intitule_rncp || b.intitule || '';
          return titleA.length - titleB.length;
        });

        // Si on a des résultats scorés
        if (scoredResults.length > 0) {
          const bestMatch = scoredResults[0];
          const score = bestMatch.relevanceScore;
          const isNonReference = score < 70;

          // ✅ Afficher le TYPE de recherche qui a trouvé le meilleur match
          const searchTypeLabel = bestMatch.searchType ? `[TYPE ${bestMatch.searchType}]` : '';
          console.log(`🎯 ${searchTypeLabel} Meilleur match: "${bestMatch.intitule_diplome || bestMatch.intitule}" (score: ${Math.round(score)})`);

          // Si le score est >= 70, enrichir avec les blocs de compétences
          let enrichedData = bestMatch;
          if (!isNonReference) {
            try {
              const enrichedResponse = await fetch(
                `${import.meta.env.VITE_API_URL}/kit-apprentissage/enriched/${bestMatch.rncp || bestMatch.code_rncp}`,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                  }
                }
              );

              const enrichedResponseData = await enrichedResponse.json();
              if (enrichedResponseData.success && enrichedResponseData.data) {
                enrichedData = enrichedResponseData.data;
                console.log(`  ✅ Blocs de compétences récupérés: ${enrichedData.blocs_competences?.length || 0} bloc(s)`);
              }
            } catch (enrichError) {
              console.warn(`  ⚠️ Impossible d'enrichir RNCP ${bestMatch.rncp}:`, enrichError.message);
            }
          }

          return {
            ...formation,
            rncpCode: bestMatch.rncp || bestMatch.code_rncp,
            rncpData: enrichedData,
            relevanceScore: score,
            isNonReference: isNonReference,
            enrichmentStatus: 'success', // vert
            searchType: bestMatch.searchType // ✅ Conserver le type de recherche
          };
        }
      }

      // Aucun résultat trouvé
      return {
        ...formation,
        rncpCode: null,
        rncpData: null,
        relevanceScore: 0,
        isNonReference: false,
        enrichmentStatus: 'not_found' // orange
      };
    } catch (error) {
      console.error(`Erreur enrichissement "${formation.title}":`, error);
      return {
        ...formation,
        rncpCode: null,
        rncpData: null,
        relevanceScore: 0,
        isNonReference: false,
        enrichmentStatus: 'not_found' // orange
      };
    }
  };

  // Fonction pour chercher l'organisme
  const handleSearchOrganism = async () => {
    const cleanSiret = siret.replace(/\s/g, '');

    if (!cleanSiret) {
      toast.error('Veuillez entrer un SIRET');
      return;
    }

    if (cleanSiret.length !== 14 || !/^\d{14}$/.test(cleanSiret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }

    setLoadingStage('search');
    setError(null);
    setOrganism(null);
    setFormations([]);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const data = await response.json();

      if (data.success && data.data) {
        setOrganism({
          name: data.data.organism?.name || 'Organisme trouvé',
          url: data.data.organismUrl,
          siret: data.data.siret,
          address: data.data.organism?.address,
          totalFormations: data.data.totalFormations
        });
        toast.success(`Organisme trouvé : ${data.data.organism?.name || 'Organisme'}`);
      } else {
        setError(data.error || 'Organisme non trouvé pour ce SIRET');
        toast.error(data.error || 'Organisme non trouvé pour ce SIRET');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError('Erreur de connexion au serveur');
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoadingStage(null);
    }
  };

  // Callback pour gérer la progression
  const handleProgressChange = (progress) => {
    // Pas besoin d'afficher les skeletons ici, on le fait manuellement
  };

  // Fonction pour récupérer ET enrichir les formations PROGRESSIVEMENT
  const handleLoadFormations = async () => {
    if (!organism) return;

    setLoadingStage('formations');
    setFormations([]);
    const estimatedCount = organism?.totalFormations || 10;

    try {
      const cleanSiret = siret.replace(/\s/g, '');

      // 1. Afficher les skeletons un par un (100ms entre chaque)
      const skeletons = [];
      for (let i = 0; i < estimatedCount; i++) {
        skeletons.push({
          id: `skeleton-${i}`,
          isSkeleton: true,
          isBlurred: true,
          enrichmentStatus: 'pending'
        });
        setFormations([...skeletons]);
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms entre chaque skeleton
      }

      // 2. Récupérer les formations (en parallèle de l'affichage des skeletons)
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/intercariforef/scrape`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ siret: cleanSiret })
        }
      );

      const data = await response.json();

      if (data.success && data.data?.formations) {
        const baseFormations = data.data.formations;

        // Dédupliquer les formations par titre (en gardant les infos de lieux)
        const formationsMap = new Map();

        baseFormations.forEach(formation => {
          const normalizedTitle = formation.title.trim().toLowerCase();

          if (formationsMap.has(normalizedTitle)) {
            // Formation déjà présente, ajouter le lieu à la liste
            const existing = formationsMap.get(normalizedTitle);
            if (formation.location && !existing.locations.includes(formation.location)) {
              existing.locations.push(formation.location);
            }
          } else {
            // Nouvelle formation, créer l'entrée
            formationsMap.set(normalizedTitle, {
              ...formation,
              locations: formation.location ? [formation.location] : [],
              originalLocation: formation.location // Garder l'info originale
            });
          }
        });

        // Convertir en tableau de formations uniques
        const uniqueFormations = Array.from(formationsMap.values());

        toast.success(`${baseFormations.length} formation(s) récupérée(s) → ${uniqueFormations.length} formation(s) unique(s)`);

        // 3. Remplacer les skeletons un par un par les vraies données (avec blur 15%)
        const formationsWithBlur = [];
        for (let i = 0; i < uniqueFormations.length; i++) {
          formationsWithBlur.push({
            ...uniqueFormations[i],
            id: i,
            enrichmentStatus: 'pending',
            isBlurred: true, // blur 15% initial
            isSkeleton: false
          });
          setFormations([...formationsWithBlur]);
          await new Promise(resolve => setTimeout(resolve, 50)); // 50ms entre chaque remplacement
        }

        // 4. Enrichir progressivement (une par une avec délai de 3s)
        setEnrichmentProgress({ current: 0, total: uniqueFormations.length });

        for (let i = 0; i < uniqueFormations.length; i++) {
          // Mettre le statut en "loading"
          setFormations(prev => prev.map((f, idx) =>
            idx === i ? { ...f, enrichmentStatus: 'loading' } : f
          ));

          // Enrichir la formation
          const enriched = await enrichFormationWithRncp(uniqueFormations[i], i);

          // Mettre à jour ET retirer le blur (enrichissement terminé)
          setFormations(prev => prev.map((f, idx) =>
            idx === i ? { ...enriched, isBlurred: false } : f
          ));

          // Mettre à jour le progrès
          setEnrichmentProgress({ current: i + 1, total: uniqueFormations.length });
        }

        // 5. Terminer
        setLoadingStage(null);
        toast.success('Enrichissement terminé !');
      } else {
        toast.error('Impossible de charger les formations');
        setLoadingStage(null);
      }
    } catch (err) {
      console.error('Erreur:', err);
      toast.error('Erreur lors du chargement des formations');
      setLoadingStage(null);
    }
  };

  // Reset
  const handleReset = () => {
    setSiret('');
    setOrganism(null);
    setFormations([]);
    setError(null);
    setShowSummary(false);
  };

  // Recherche manuelle RNCP
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const [manualSearchResults, setManualSearchResults] = useState([]);
  const [isSearchingManually, setIsSearchingManually] = useState(false);

  const handleManualSearch = async () => {
    if (!manualSearchQuery.trim()) {
      toast.error('Veuillez saisir un intitulé de formation');
      return;
    }

    setIsSearchingManually(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/kit-apprentissage/search/intitule?q=${encodeURIComponent(manualSearchQuery)}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      if (data.success && data.data) {
        setManualSearchResults(data.data);
        if (data.data.length === 0) {
          toast.error('Aucune formation trouvée');
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la recherche');
    } finally {
      setIsSearchingManually(false);
    }
  };

  const handleSelectManualResult = (rncpData) => {
    if (!selectedForManualSearch) return;

    // Mettre à jour la formation avec le RNCP sélectionné
    setFormations(prev => prev.map(f =>
      f === selectedForManualSearch
        ? { ...f, rncpCode: rncpData.rncp, rncpData, enrichmentStatus: 'success' }
        : f
    ));

    toast.success(`RNCP ${rncpData.rncp} associé à la formation`);
    setSelectedForManualSearch(null);
    setManualSearchQuery('');
    setManualSearchResults([]);
  };

  // Exemples de SIRET
  const exampleSirets = [
    { label: 'GIP FCIP', siret: '18974221600022' },
    { label: 'Exemple 2', siret: '79279132900016' },
    { label: 'Exemple 3', siret: '13002526500013' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-primary-600" />
            Test Intercarif Oref
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Recherchez les formations d'un organisme et enrichissez-les avec les codes RNCP
          </p>
        </div>

        {/* Contenu principal */}
        <div className="bg-white rounded-lg shadow">

          {/* Section recherche */}
          {!organism && (
            <div className="p-6">
              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
                <Search className="w-5 h-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Rechercher un organisme de formation
                </h2>
              </div>

              {/* Barre de progression */}
              <SmartProgressBar
                isActive={loadingStage === 'search'}
                stage="search"
                enrichmentProgress={enrichmentProgress}
                formations={formations}
                onProgressChange={handleProgressChange}
              />

              <div className="space-y-6">
                {/* Input SIRET */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Numéro SIRET
                  </label>
                  <input
                    type="text"
                    value={siret}
                    onChange={(e) => setSiret(formatSiret(e.target.value))}
                    placeholder="123 456 789 01234"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    disabled={loadingStage === 'search'}
                    onKeyPress={(e) => e.key === 'Enter' && !loadingStage && handleSearchOrganism()}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Le SIRET doit contenir exactement 14 chiffres
                  </p>
                </div>

                {/* Exemples */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Exemples de SIRET :</p>
                  <div className="flex flex-wrap gap-2">
                    {exampleSirets.map((example, index) => (
                      <button
                        key={index}
                        onClick={() => setSiret(formatSiret(example.siret))}
                        className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                        disabled={loadingStage === 'search'}
                      >
                        {example.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bouton recherche */}
                <button
                  onClick={handleSearchOrganism}
                  disabled={loadingStage === 'search' || !siret}
                  className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {loadingStage === 'search' ? (
                    <>
                      <Sparkles className="w-5 h-5 animate-pulse" />
                      Recherche en cours...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      Rechercher l'organisme
                    </>
                  )}
                </button>

                {/* Erreur */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Résultat organisme */}
          {organism && formations.length === 0 && (
            <div className="p-6">
              <button
                onClick={handleReset}
                className="mb-6 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Nouvelle recherche
              </button>

              <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
                <Building2 className="w-5 h-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Organisme trouvé
                </h2>
              </div>

              {/* Barre de progression */}
              <SmartProgressBar
                isActive={loadingStage === 'formations' || loadingStage === 'enrichment'}
                stage={loadingStage}
                enrichmentProgress={enrichmentProgress}
                formations={formations}
                onProgressChange={handleProgressChange}
              />

              <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg p-6 mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{organism.name}</h3>

                <div className="space-y-3 mb-6">
                  {organism.address && (
                    <div className="flex items-start gap-2 text-gray-700">
                      <MapPin className="w-5 h-5 text-primary-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{organism.address}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-gray-700">
                    <Award className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <span className="text-sm font-medium">SIRET : {formatSiret(organism.siret)}</span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-700">
                    <BookOpen className="w-5 h-5 text-primary-600 flex-shrink-0" />
                    <span className="text-sm font-semibold">{organism.totalFormations} formation(s) disponible(s)</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleLoadFormations}
                    disabled={loadingStage === 'formations' || loadingStage === 'enrichment'}
                    className="flex-1 bg-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {loadingStage ? (
                      <>
                        <Sparkles className="w-5 h-5 animate-pulse" />
                        {loadingStage === 'formations' ? 'Chargement...' : 'Enrichissement RNCP...'}
                      </>
                    ) : (
                      <>
                        <GraduationCap className="w-5 h-5" />
                        Voir les {organism.totalFormations} formations
                      </>
                    )}
                  </button>

                  <a
                    href={organism.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Voir sur Intercarif
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Liste formations */}
          {formations.length > 0 && !showSummary && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-gray-400" />
                  <h2 className="text-lg font-semibold text-gray-900">
                    {formations.length} Formation(s) - {organism.name}
                  </h2>
                </div>
                <button
                  onClick={handleReset}
                  className="text-sm text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Nouvelle recherche
                </button>
              </div>

              {/* Barre de progression visible pendant l'enrichissement */}
              {enrichmentProgress && enrichmentProgress.total > 0 && (
                <SmartProgressBar
                  isActive={true}
                  stage="enrichment"
                  enrichmentProgress={enrichmentProgress}
                  formations={formations}
                  onProgressChange={handleProgressChange}
                />
              )}

              <div className="space-y-4 mb-6">
                {formations.map((formation, index) => {
                  // Si c'est un skeleton
                  if (formation.isSkeleton) {
                    return <FormationSkeletonCard key={formation.id} index={index} isBlurred={true} />;
                  }

                  // Sinon, vraie formation
                  const isNonFoundOrNonRef = !formation.rncpCode || formation.isNonReference;
                  const borderColor = isNonFoundOrNonRef ? 'border-orange-300' : 'border-gray-200';
                  const bgColor = isNonFoundOrNonRef ? 'bg-orange-50' : 'bg-white';
                  const hoverBorder = isNonFoundOrNonRef ? 'hover:border-orange-400' : 'hover:border-primary-300';

                  return (
                    <div
                      key={formation.id || index}
                      className={`border ${borderColor} ${bgColor} rounded-lg p-5 ${hoverBorder} hover:shadow-md transition-all duration-500 ${
                        formation.isBlurred ? 'blur-15' : 'blur-none'
                      }`}
                      style={{
                        animation: `slideDown 0.5s ease-out ${index * 0.05}s both`
                      }}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <EnrichmentStatusIcon
                          status={formation.enrichmentStatus}
                          onClick={() => setSelectedForManualSearch(formation)}
                        />
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-gray-900 mb-2">
                            {formation.title || 'Formation sans titre'}
                          </h3>

                          {/* Affichage des lieux multiples */}
                          {formation.locations && formation.locations.length > 0 && (
                            <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                              <MapPin className="w-4 h-4 text-gray-500" />
                              <span>
                                {formation.locations.length === 1 ? (
                                  formation.locations[0]
                                ) : (
                                  `${formation.locations.length} lieu(x) : ${formation.locations.join(', ')}`
                                )}
                              </span>
                            </div>
                          )}

                        <div className="flex flex-wrap gap-2 mb-3">
                          {formation.rncpCode && (
                            <>
                              <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm font-medium">
                                RNCP: {formation.rncpCode}
                              </span>
                              {formation.isNonReference && (
                                <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                                  (non référencé)
                                </span>
                              )}
                              {formation.relevanceScore !== undefined && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                                  Score: {Math.round(formation.relevanceScore)}%
                                </span>
                              )}
                              {formation.searchType && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-bold">
                                  TYPE {formation.searchType}
                                </span>
                              )}
                            </>
                          )}
                          {!formation.rncpCode && formation.enriched === false && (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                              RNCP non trouvé
                            </span>
                          )}
                        </div>

                        {(formation.code || formation.domain || formation.level || formation.duration) && (
                          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                            {formation.code && (
                              <div className="flex items-center gap-2">
                                <Award className="w-4 h-4 text-primary-500" />
                                <span><strong>Code :</strong> {formation.code}</span>
                              </div>
                            )}
                            {formation.domain && (
                              <div className="flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-green-500" />
                                <span><strong>Domaine :</strong> {formation.domain}</span>
                              </div>
                            )}
                            {formation.level && (
                              <div className="flex items-center gap-2">
                                <GraduationCap className="w-4 h-4 text-purple-500" />
                                <span><strong>Niveau :</strong> {formation.level}</span>
                              </div>
                            )}
                            {formation.duration && (
                              <div className="flex items-center gap-2">
                                <span className="w-4 h-4 text-orange-500">📅</span>
                                <span><strong>Durée :</strong> {formation.duration}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        {formation.url && (
                          <a
                            href={formation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 whitespace-nowrap text-sm font-medium"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Détails
                          </a>
                        )}
                        {formation.rncpData && formation.rncpData.blocs_competences && formation.rncpData.blocs_competences.length > 0 && (
                          <button
                            onClick={() => setSelectedFormation(formation)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 whitespace-nowrap text-sm font-medium"
                          >
                            <FileText className="w-4 h-4" />
                            Compétences
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>

              {/* Bouton "Voir la liste" */}
              <div className="border-t border-gray-200 pt-6">
                <button
                  onClick={() => setShowSummary(true)}
                  className="w-full bg-gradient-to-r from-primary-600 to-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-primary-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                >
                  <List className="w-6 h-6" />
                  Voir la liste complète (formations + blocs + compétences)
                </button>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    <span>
                      Source :
                      <a
                        href={organism.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 font-medium ml-1"
                      >
                        {organism.name} sur Intercarif Oref
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Modales */}
      {selectedFormation && (
        <CompetenceBlocksView
          formation={selectedFormation}
          onClose={() => setSelectedFormation(null)}
        />
      )}

      {showSummary && (
        <SummaryListView
          formations={formations}
          organism={organism}
          onClose={() => setShowSummary(false)}
        />
      )}

      {/* Modal de recherche manuelle RNCP */}
      {selectedForManualSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  Recherche manuelle RNCP
                </h3>
                <button
                  onClick={() => {
                    setSelectedForManualSearch(null);
                    setManualSearchQuery('');
                    setManualSearchResults([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Formation: <strong>{selectedForManualSearch?.title}</strong>
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualSearchQuery}
                  onChange={(e) => setManualSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleManualSearch()}
                  placeholder="Rechercher un intitulé de formation..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <button
                  onClick={handleManualSearch}
                  disabled={isSearchingManually}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSearchingManually ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Recherche...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Rechercher
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[50vh]">
              {manualSearchResults.length > 0 ? (
                <div className="space-y-3">
                  {manualSearchResults.map((result, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectManualResult(result)}
                      className="border border-gray-200 rounded-lg p-4 hover:border-primary-500 hover:bg-primary-50 cursor-pointer transition-all"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 bg-primary-100 text-primary-800 rounded text-xs font-bold">
                              {result.rncp}
                            </span>
                            {result.active && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                Actif
                              </span>
                            )}
                          </div>
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {result.intitule}
                          </h4>
                          {result.niveau && (
                            <p className="text-sm text-gray-600">
                              Niveau: {result.niveau}
                            </p>
                          )}
                        </div>
                        <button className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm">
                          Sélectionner
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <Search className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>Recherchez un intitulé de formation pour trouver le code RNCP correspondant</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
